import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useFxRates } from "@/features/ledger/api/use-fx-rates";
import type { FxRateFilter } from "@/features/ledger/api/dto";
import {
  FxRateFormDialog,
  fxRateRowId,
  fxRatesColumns,
} from "@/features/ledger/components";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";

/** Traduit les filtres de colonnes DataTableClar en `FxRateFilter` (§4.2). */
const toFxRateFilter = (filters: ColumnFiltersState): FxRateFilter => {
  const filter: FxRateFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "currencyFrom") filter.currencyFrom = value;
    if (id === "currencyTo") filter.currencyTo = value;
  }
  return filter;
};

/**
 * Historique des taux de change par couple de devises
 * (`POST /FxRates/all`, guide §6.4) — lecture authentifiée ; enregistrement
 * réservé à flashpay-superadmin / flashpay-admin.
 */
export function FxRatesPage() {
  const { t } = useLedgerTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toFxRateFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useFxRates(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("ledger.fxRates.title")}
        </h1>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("ledger.fxRates.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).map((r) => ({
          ...r,
          id: fxRateRowId(r),
        }))}
        columnsClar={fxRatesColumns}
        title={t("ledger.fxRates.title")}
        exportFileName="fx-rates"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <FxRateFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
